import { useEffect, useState } from 'react'
import { supabase } from './supabase'
import { perfMarkOnce } from './perfTrace'

// Avatar degli altri utenti per classifiche e liste: una sola richiesta per gli id mancanti, poi in memoria
// (tra una tappa e l'altra non si riscarica). dim_user e' leggibile da ogni utente loggato (policy "Profili visibili").
// Ritorna { [id_user]: avatar_settings } — l'avatar_settings puo' mancare (nessun salvataggio): si usa il default dall'id.
const cache = new Map()    // id_user -> avatar_settings | null
const inflight = new Set()
const listeners = new Set()
const CHUNK = 150

async function fetchMissing(ids) {
  const missing = ids.filter(id => !cache.has(id) && !inflight.has(id))
  if (!missing.length) return
  missing.forEach(id => inflight.add(id))
  perfMarkOnce('avatars_start')
  try {
    for (let i = 0; i < missing.length; i += CHUNK) {
      const part = missing.slice(i, i + CHUNK)
      const { data } = await supabase.rpc('fn_user_avatars', { p_ids: part })
      const found = new Set()
      ;(data ?? []).forEach(r => { cache.set(r.id_user, r.avatar_settings ?? null); found.add(r.id_user) })
      part.forEach(id => { if (!found.has(id)) cache.set(id, null) })
    }
  } catch { /* nessun avatar: restano i default */ }
  finally {
    missing.forEach(id => inflight.delete(id))
    perfMarkOnce('avatars_done')
    listeners.forEach(fn => fn())
  }
}

export function useAvatars(userIds) {
  const [, bump] = useState(0)
  const key = [...new Set((userIds ?? []).filter(Boolean))].sort().join(',')
  useEffect(() => {
    const fn = () => bump(v => v + 1)
    listeners.add(fn)
    return () => { listeners.delete(fn) }
  }, [])
  useEffect(() => {
    if (key) fetchMissing(key.split(','))
  }, [key])
  return Object.fromEntries((userIds ?? []).filter(id => cache.has(id)).map(id => [id, cache.get(id)]))
}

// Dopo il salvataggio dall'editor la cache dell'utente va aggiornata
export function setCachedAvatar(userId, avatarSettings) {
  cache.set(userId, avatarSettings ?? null)
  listeners.forEach(fn => fn())
}
